import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/announcements")({ component: AdminAnn });

function AdminAnn() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({
    queryKey: ["admin-ann"],
    queryFn: async () => (await supabase.from("announcements").select("*").order("created_at", { ascending: false })).data ?? [],
  });
  const create = useMutation({
    mutationFn: async (input: { title: string; body: string }) => {
      const { error } = await supabase.from("announcements").insert(input);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Posted"); qc.invalidateQueries({ queryKey: ["admin-ann"] }); qc.invalidateQueries({ queryKey: ["announcements"] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { await supabase.from("announcements").delete().eq("id", id); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-ann"] }); qc.invalidateQueries({ queryKey: ["announcements"] }); },
  });
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <form className="glass rounded-2xl p-6 space-y-4" onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        create.mutate({ title: String(fd.get("title")), body: String(fd.get("body")) });
        (e.currentTarget as HTMLFormElement).reset();
      }}>
        <div className="text-sm font-medium">New announcement</div>
        <div className="space-y-2"><Label>Title</Label><Input name="title" required /></div>
        <div className="space-y-2"><Label>Body</Label><Textarea name="body" rows={5} required /></div>
        <Button type="submit">Publish</Button>
      </form>
      <div className="glass rounded-2xl p-6 space-y-3">
        <div className="text-sm font-medium">Published</div>
        {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">None</div> : rows.map((a) => (
          <div key={a.id} className="rounded-lg bg-white/5 p-3">
            <div className="flex justify-between items-start gap-2">
              <div className="text-sm font-medium">{a.title}</div>
              <button onClick={() => del.mutate(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{a.body}</div>
            <div className="text-[10px] text-muted-foreground mt-2">{fmtDate(a.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}